import { useRef, useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PublicFooter from '../../components/layout/PublicFooter';
import { getPathBySlug, learningPaths, isArticlePublished } from './content';
import { publicArticlesApi } from '../../services/api/articlesApi';
import type { ArticleMeta } from './types';
import { usePageMeta } from '../../hooks/usePageMeta';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(18px)',
    transition: 'opacity 0.7s ease, transform 0.7s ease',
  };
  return { ref, style };
}

const difficultyColor: Record<string, string> = {
  beginner: 'text-emerald-400 border-emerald-400/30',
  intermediate: 'text-blue-400 border-blue-400/30',
  advanced: 'text-amber-400 border-amber-400/30',
};

const difficultyLabel: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

export default function LearnCategoryPage() {
  const { category } = useParams<{ category: string }>();
  const path = category ? getPathBySlug(category) : undefined;
  usePageMeta({ title: path ? path.title : 'Learn', description: path ? path.description : '' });
  const revHero = useScrollReveal(0.1);
  const revArticles = useScrollReveal(0.1);

  // Fetch articles from API and overlay onto static path.articles
  const [apiArticles, setApiArticles] = useState<Map<string, ArticleMeta>>(new Map());
  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    publicArticlesApi.list(category)
      .then((res) => {
        if (!cancelled && res.success) {
          const map = new Map<string, ArticleMeta>();
          for (const a of res.articles) map.set(a.slug, a);
          setApiArticles(map);
        }
      })
      .catch(() => { /* keep static fallback */ });
    return () => { cancelled = true; };
  }, [category]);

  if (!path) return <Navigate to="/learn" replace />;

  // Merge: use API data for metadata when available, keep path ordering
  const articles = path.articles.map((staticA) => {
    const apiA = apiArticles.get(staticA.slug);
    return apiA ? { ...staticA, ...apiA } : staticA;
  });

  // An article is published if it exists in the API response OR is marked published in static
  const isPublished = (slug: string) => apiArticles.has(slug) || isArticlePublished(slug);

  // Find next/prev paths for navigation
  const pathIndex = learningPaths.findIndex((p) => p.slug === path.slug);
  const prevPath = pathIndex > 0 ? learningPaths[pathIndex - 1] : null;
  const nextPath =
    pathIndex < learningPaths.length - 1 ? learningPaths[pathIndex + 1] : null;

  return (
    <div className="min-h-screen bg-[#0a0e14] text-white selection:bg-[#9d4edd]/30 selection:text-white">
      <PublicNavBar />

      {/* Hero */}
      <section
        ref={revHero.ref as React.RefObject<HTMLElement>}
        style={revHero.style}
        className="pt-32 pb-16 px-4 sm:px-6 max-w-4xl mx-auto"
      >
        <Link
          to="/learn"
          className="inline-flex items-center gap-2 text-[12px] text-gray-500 hover:text-gray-300 transition-colors mb-6"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />
          All learning paths
        </Link>

        <div className="flex items-center gap-4 mb-4">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${path.color}15` }}
          >
            <FontAwesomeIcon
              icon={path.icon}
              className="text-sm"
              style={{ color: path.color }}
            />
          </div>
          <div>
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-gray-500 block">
              {path.subtitle}
            </span>
            <h1 className="text-[24px] sm:text-[28px] font-semibold tracking-tight">
              {path.title}
            </h1>
          </div>
        </div>

        <p className="text-[15px] text-gray-400 leading-relaxed max-w-2xl">
          {path.description}
        </p>
      </section>

      {/* Articles */}
      <section
        ref={revArticles.ref as React.RefObject<HTMLElement>}
        style={revArticles.style}
        className="pb-20 px-4 sm:px-6 max-w-4xl mx-auto"
      >
        <div className="space-y-4">
          {articles.map((article, i) => {
            const published = isPublished(article.slug);
            if (published) {
              return (
                <Link
                  key={article.slug}
                  to={`/learn/${path.slug}/${article.slug}`}
                  className="group flex items-start gap-5 border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-colors bg-gray-900/30"
                >
                  <span className="w-8 h-8 rounded-full border border-gray-700 flex items-center justify-center text-[13px] text-gray-500 shrink-0 mt-0.5 group-hover:border-gray-600 transition-colors">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1.5">
                      <h2 className="text-[15px] font-semibold text-white group-hover:text-[#9d4edd] transition-colors">
                        {article.title}
                      </h2>
                      <span
                        className={`text-[10px] font-mono border rounded-full px-2 py-0.5 ${difficultyColor[article.difficulty]}`}
                      >
                        {difficultyLabel[article.difficulty]}
                      </span>
                    </div>
                    <p className="text-[13px] text-gray-400 leading-relaxed">
                      {article.description}
                    </p>
                    <span className="text-[11px] text-gray-600 font-mono mt-2 block">
                      {article.readTime} read
                    </span>
                  </div>
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-xs text-gray-600 group-hover:text-[#9d4edd] mt-2 transition-colors"
                  />
                </Link>
              );
            }
            return (
              <div
                key={article.slug}
                className="group flex items-start gap-5 border border-gray-800/50 rounded-xl p-5 bg-gray-900/15 cursor-default"
              >
                <span className="w-8 h-8 rounded-full border border-gray-800 flex items-center justify-center text-[13px] text-gray-600 shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5">
                    <h2 className="text-[15px] font-semibold text-gray-500">
                      {article.title}
                    </h2>
                    <span className="text-[9px] font-mono tracking-wider uppercase border border-gray-700/50 text-gray-600 rounded-full px-2 py-0.5">
                      Coming soon
                    </span>
                  </div>
                  <p className="text-[13px] text-gray-600 leading-relaxed">
                    {article.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Path navigation */}
      <section className="pb-24 px-4 sm:px-6 max-w-4xl mx-auto border-t border-gray-800/40 pt-10">
        <div className="flex justify-between items-center">
          {prevPath ? (
            <Link
              to={`/learn/${prevPath.slug}`}
              className="flex items-center gap-2 text-[13px] text-gray-400 hover:text-white transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
              {prevPath.title}
            </Link>
          ) : (
            <div />
          )}
          {nextPath ? (
            <Link
              to={`/learn/${nextPath.slug}`}
              className="flex items-center gap-2 text-[13px] text-gray-400 hover:text-white transition-colors"
            >
              {nextPath.title}
              <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
            </Link>
          ) : (
            <div />
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
